import React, { useState } from "react";
import Modal from "react-modal";
import { useDropzone } from "react-dropzone";
import "cropperjs/dist/cropper.css";
import axios from "axios";
import ProgressBar from "components/ProgressBar";
import { Img } from "components/Img";
import { showGlobalModal } from "components/GlobalModal";
import { renderBaseUrlForApi } from "utils";
import { Upload } from "lucide-react";
import { convertImageToWebP } from "utils/convertImageToWebP";

Modal.setAppElement("#root");

const FOTOR_ACCESS_KEY = "sk_wK3iRjOwNEosEZ2QcLgCQIpe";

function getFileNameFromUrl(url) {
    return url ? url.substring(url.lastIndexOf('/') + 1) : "Unknown file";
}

const FotorImageUpload: React.FC<{
    onChange: (file: object) => void;
    imageValue: {
        originalImage: string;
        selectedImage: string;
        enhancedImage: string;
    };
    label?: string;
    customMessage?: string;
    wantHDOption?: boolean;
    previewNotRequired?: boolean;
    disable?: boolean;
    labelClassNames?: string;
    containerHeight?: string | any;
    maxSize?: number;
    isError?: boolean;
    accept?: { [key: string]: string[] };
}> = ({
    onChange,
    imageValue,
    label,
    customMessage,
    wantHDOption = true,
    disable,
    maxSize = 5,
    isError,
    containerHeight = "6.48vh",
    accept = { "image/*": [] },
}) => {
        const [modalIsOpen, setModalIsOpen] = useState(false);
        const [showLowResModal, setShowLowResModal] = useState(false);
        const [loader, setLoader] = useState(false);
        const [enhancementStatus, setEnhancementStatus] = useState<{
            processing: boolean;
            taskId?: string;
        }>({ processing: false });
        const [uploadController, setUploadController] = useState<AbortController | null>(null);

        const IS_ORIGINAL_IMAGE = Boolean(
            imageValue?.selectedImage === imageValue?.originalImage
        );
        const IS_ENHANCED_IMAGE = Boolean(
            imageValue?.selectedImage === imageValue?.enhancedImage
        );

        const handleImageUpload = async (file: File) => {
            const controller = new AbortController();
            setUploadController(controller);

            setLoader(true);

            try {
                const webPFile = await convertImageToWebP(file);
                const formData = new FormData();
                formData.append("files", webPFile);
                const uploadResponse = await axios.post(
                    `${renderBaseUrlForApi()}/master/upload/webTempFiles`,
                    formData,
                    {
                        headers: { "Custom-Header": "multipart/form-data" },
                        signal: controller.signal
                    }
                );

                const imageUrl = uploadResponse?.data?.data?.[0];
                onChange({
                    originalImage: imageUrl,
                    selectedImage: imageUrl,
                    enhancedImage: "",
                });
                setModalIsOpen(true);
            } catch (error: any) {
                if (axios.isCancel(error)) {
                    console.log("Upload aborted:", error.message);
                } else {
                    console.error("Upload error:", error);
                    showGlobalModal(error?.response?.data?.message || "Upload failed!", "error");
                }
            } finally {
                setLoader(false);
            }
        };

        const handleCancelUpload = () => {
            if (uploadController) {
                uploadController.abort();
                setUploadController(null);
                setLoader(false);
            }
        };

        const handleEnhanceImage = async () => {
            if (!imageValue?.originalImage) return;

            try {
                setEnhancementStatus({ processing: true, taskId: "" });
                console.log("[ENHANCE] Starting enhancement for:", imageValue.originalImage);

                const response = await axios.post(
                    `${renderBaseUrlForApi()}/master/fr/imageEnhance`,
                    {
                        user_image_url: imageValue.originalImage,
                        face_upsample: true,
                        background_enhance: true,
                        upscale: 2,
                        fidelity_weight: 0.5,
                    },
                    {
                        headers: {
                            Authorization: `Bearer ${FOTOR_ACCESS_KEY}`,
                            "Content-Type": "application/json",
                        },
                    }
                );

                const taskId = response.data?.data;
                if (!taskId) throw new Error("No task ID returned from enhancement API");

                console.log("[ENHANCE] Enhancement task submitted. Task ID:", taskId);
                setEnhancementStatus({ processing: true, taskId });

                startPollingEnhancementStatus(taskId);
            } catch (error: any) {
                console.error("[ENHANCE ERROR]", error);
                showGlobalModal(error?.response?.data?.message || "Unknown error occurred", "error");
                setEnhancementStatus({ processing: false, taskId: "" });
            }
        };

        const startPollingEnhancementStatus = (taskId: string) => {
            console.log("[POLLING] Started for Task ID:", taskId);
            let attempts = 0;
            const maxAttempts = 20;

            const intervalId = setInterval(async () => {
                try {
                    console.log(`[POLLING] Attempt #${attempts + 1}`);
                    const res = await axios.get(`https://api-b.fotor.com/v1/aiart/tasks/${taskId}`, {
                        headers: { Authorization: `Bearer ${FOTOR_ACCESS_KEY}` },
                    });

                    const data = res.data?.data;
                    console.log("[POLLING] Response data:", data);

                    const status = data?.status;
                    const resultUrl = data?.resultUrl;

                    if (status === 1 && resultUrl) {
                        clearInterval(intervalId);
                        console.log("[POLLING] Enhancement completed:", resultUrl);

                        onChange({
                            originalImage: imageValue.originalImage,
                            selectedImage: imageValue.selectedImage,
                            enhancedImage: resultUrl
                        });
                        setEnhancementStatus({ processing: false, taskId: "" });
                    } else if (status === 2) {
                        clearInterval(intervalId);
                        console.error("[POLLING] Enhancement failed.");
                        showGlobalModal("Fotor task could not complete successfully", "error");
                        setEnhancementStatus({ processing: false, taskId: "" });
                    } else {
                        console.log(`[POLLING] Status: ${status}. Still processing...`);
                    }

                    if (++attempts >= maxAttempts) {
                        clearInterval(intervalId);
                        console.warn("[POLLING] Max attempts reached without success.");
                        setEnhancementStatus({ processing: false, taskId: "" });
                        showGlobalModal("It's taking longer than expected. Please try again later.", "warning");
                    }
                } catch (err: any) {
                    clearInterval(intervalId);
                    console.error("[POLLING ERROR]", err);
                    setEnhancementStatus({ processing: false, taskId: "" });
                    showGlobalModal(err?.response?.data?.message || "Polling failed unexpectedly", "error");
                }
            }, 3000);
        };

        const handleSelection = (url: string) => {
            onChange({
                ...imageValue,
                selectedImage: url,
            });
        };

        const handleModalCancel = () => {
            setModalIsOpen(false);
        };

        const handleSaveEnhancedImage = () => {
            setModalIsOpen(false);
        };

        const handleDeleteTheUploadedFile = () => {
            onChange({
                originalImage: "",
                selectedImage: "",
                enhancedImage: "",
            });
        };
        const { getRootProps, getInputProps, isDragActive } = useDropzone({
            onDrop: (acceptedFiles) => {
                if (acceptedFiles.length === 0) return;

                if (acceptedFiles[0]?.size > maxSize * 1024 * 1024) {
                    showGlobalModal(`File exceeds ${maxSize}MB`, "error");
                    return;
                }
                handleImageUpload(acceptedFiles[0]);
            },
            onDropRejected: (fileRejections) => {
                // Give user feedback if they select the wrong file type
                const error = fileRejections[0]?.errors[0];
                if (error?.code === 'file-invalid-type') {
                    showGlobalModal("Invalid file type. Please upload allowed formats.", "error");
                } else {
                    showGlobalModal(error?.message || "File upload rejected", "error");
                }
            },
            accept: accept, // <-- USE THE PROP HERE
            multiple: false,
            disabled: disable || loader
        });

        React.useEffect(() => {
            if (imageValue?.originalImage) {
                setShowLowResModal(true);
            }
        }, [imageValue?.originalImage]);

        return (
            <>
                <div
                    {...getRootProps()}
                    className={`
                    relative w-[100%] p-[0.4455vw]
                    border border-dashed rounded-[0.3645vw]
                    flex items-center justify-between h-[10vh]
                    transition-colors duration-200 ease-in-out bg-[#F9FAFB]
                    ${(disable || loader) ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}
                    ${isError ? 'border-red-500 bg-red-50' :
                            isDragActive ? 'border-blue-500 bg-blue-50' :
                                'border-[#C4C2BA] hover:bg-gray-50 bg-white'}
                `}
                    style={containerHeight ? { minHeight: containerHeight } : {}}
                >
                    <input {...getInputProps()} />

                    <div className="flex w-full h-full items-center ">
                        {!imageValue?.originalImage ? (
                            <div className="flex justify-between items-center w-full ">
                                {/* LOADER STATE */}
                                {loader ? (
                                    <div className="flex items-center w-full gap-[0.729vw]">
                                        <div className="flex-grow">
                                            <ProgressBar />
                                        </div>
                                        <button
                                            onClick={(e) => { e.stopPropagation(); handleCancelUpload(); }}
                                            type="button"
                                            className="flex-shrink-0 p-[0.3645vw] hover:bg-gray-200 rounded-full transition-colors"
                                        >
                                            <Img src="/images/new-landing-page/blackCloseButton.svg" alt="Cancel" className="w-[0.891vw] h-[0.891vw] object-contain" />
                                        </button>
                                    </div>
                                ) : (
                                    /* EMPTY STATE (Upload Logo UI) */
                                    <>
                                        <div className="flex flex-col space-y-[0.081vw]">
                                            <h3 className="font-inter font-[400] text-[#000] text-[2.52vh]">
                                                {label || "Upload Logo"}
                                            </h3>
                                            <p className="font-inter font-[300] text-[1.782vh] text-[#000]">
                                                Drag & drop your image here, or{' '}
                                                <span className="text-blue-600 hover:underline font-medium">browse files</span>
                                                {' '}( {customMessage || `JPG, JPEG, Max ${maxSize}MB`} )
                                            </p>
                                        </div>

                                        <div className="flex-shrink-0 ml-[0.729vw]">
                                            <div className="p-[0.3645vw] rounded-full hover:bg-gray-100 transition-colors">
                                                <Upload className="w-[1.053vw] h-[1.053vw] text-gray-900" />
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        ) : (
                            /* FILLED STATE (Image uploaded) */
                            <div className="flex justify-between items-center w-full">
                                <div className="flex gap-x-[0.729vw] items-center">
                                    <Img
                                        src={imageValue?.selectedImage}
                                        alt="image"
                                        className="w-[2.106vw] h-[2.106vw] min-w-[2.106vw] min-h-[2.106vw] object-cover rounded-[0.3645vw] shadow-sm"
                                    />
                                    <div className="flex flex-col">
                                        <span className="text-[0.648vw] font-medium text-gray-900 line-clamp-1">
                                            {getFileNameFromUrl(imageValue?.selectedImage)}
                                        </span>
                                    </div>
                                </div>

                                {!disable && (
                                    <div className="flex items-center gap-x-[0.3645vw]">
                                        {wantHDOption && (
                                            <button
                                                className="bg-[#5E4DE0] text-white rounded-full p-[0.3645vw] hover:bg-[#4b3db3] transition-colors shadow-sm"
                                                onClick={e => {
                                                    e.stopPropagation();
                                                    if (!disable && !loader) {
                                                        setModalIsOpen(true);
                                                    }
                                                }}
                                                type="button"
                                                title="Enhance Image"
                                            >
                                                <Img
                                                    src="/images/new-landing-page/descriptionImage.svg"
                                                    alt="enhanceImage"
                                                    className="w-[0.891vw] h-[0.891vw] object-contain invert brightness-0"
                                                />
                                            </button>
                                        )}
                                        <button
                                            disabled={disable}
                                            onClick={handleDeleteTheUploadedFile}
                                            type="button"
                                            className="p-[0.3645vw] rounded-full hover:bg-red-50 hover:text-red-500 transition-colors"
                                            title="Delete Image"
                                        >
                                            <Img
                                                src="/images/new-landing-page/delete.svg"
                                                alt="delete"
                                                className="w-[0.891vw] h-[0.891vw] object-contain"
                                            />
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {wantHDOption && (
                    <Modal
                        isOpen={modalIsOpen}
                        onRequestClose={handleModalCancel}
                        contentLabel="Enhance Image"
                        className="fixed inset-0 flex items-center justify-center z-40 font-manrope backdrop-blur-sm"
                        overlayClassName="fixed inset-0 bg-black/90 z-[1001] transition-opacity duration-300"
                        closeTimeoutMS={300}
                    >
                        <div className="bg-[white] p-[1.053vw] rounded-[0.5265vw] w-[38.88%] max-w-[34.506vw] shadow-2xl border border-white/10">
                            <div className="grid grid-cols-2 gap-[1.053vw]">
                                {/* Original Image Section */}
                                <div className="group relative">
                                    <h3 className="text-[0.81vw] font-semibold text-gray-800 mb-[0.5265vw] flex items-center gap-[0.3645vw]">
                                        <span className="w-[0.3645vw] h-[0.3645vw] bg-blue-500 rounded-full"></span>
                                        Original Image
                                        {IS_ORIGINAL_IMAGE && (
                                            <span className="ml-[0.3645vw] text-[0.5265vw] bg-blue-100 text-blue-800 py-[0.162vw] px-[0.3645vw] rounded-full">Selected</span>
                                        )}
                                    </h3>
                                    <div className={`relative aspect-auto rounded-[0.5265vw] overflow-hidden transition-all duration-300 ${IS_ORIGINAL_IMAGE
                                        ? "border-[0.162vw] border-blue-500 shadow-lg shadow-blue-200"
                                        : "border-[0.081vw] border-gray-100 bg-gray-50"
                                        }`}>
                                        {imageValue?.originalImage && (
                                            <div className="relative">
                                                <img
                                                    src={imageValue.originalImage}
                                                    alt="Original"
                                                    className={`w-full h-full object-cover cursor-pointer transition-transform duration-300 hover:scale-105`}
                                                    onClick={() => handleSelection(imageValue.originalImage)}
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Enhanced Image Section */}
                                <div className="group relative">
                                    <h3 className="text-[0.81vw] font-semibold text-gray-800 mb-[0.5265vw] flex items-center gap-[0.3645vw]">
                                        <span className="w-[0.3645vw] h-[0.3645vw] bg-purple-500 rounded-full"></span>
                                        Enhanced Image
                                        {IS_ENHANCED_IMAGE && imageValue?.enhancedImage && (
                                            <span className="ml-[0.3645vw] text-[0.5265vw] bg-purple-100 text-purple-800 py-[0.162vw] px-[0.3645vw] rounded-full">Selected</span>
                                        )}
                                    </h3>
                                    <div className={`relative aspect-auto rounded-[0.5265vw] overflow-hidden transition-all duration-300 ${IS_ENHANCED_IMAGE && imageValue?.enhancedImage
                                        ? "border-[0.162vw] border-purple-500 shadow-lg shadow-purple-200"
                                        : "border-[0.081vw] border-gray-100 bg-gradient-to-br from-gray-50 to-gray-100"
                                        }`}>
                                        {imageValue?.enhancedImage ? (
                                            <div className="relative">
                                                <img
                                                    src={imageValue.enhancedImage}
                                                    alt="Enhanced"
                                                    className={`w-full h-full object-cover cursor-pointer transition-transform duration-300 hover:scale-105`}
                                                    onClick={() => handleSelection(imageValue.enhancedImage)}
                                                />
                                            </div>
                                        ) : (
                                            <div className="h-full flex flex-col items-center justify-center p-[0.729vw] relative">
                                                <div className="flex flex-col items-center justify-center gap-[0.729vw]">
                                                    <button
                                                        type="button"
                                                        className={`transition-transform ${enhancementStatus.processing ? "cursor-not-allowed" : "hover:scale-110"
                                                            }`}
                                                        onClick={(e) => !enhancementStatus.processing && handleEnhanceImage()}
                                                        disabled={enhancementStatus.processing}
                                                    >
                                                        {enhancementStatus.processing ? (
                                                            <div className="p-[0.729vw]">
                                                                <img src="/images/loader.gif" className="w-[1.377vw] h-[1.377vw]" alt="Processing" />
                                                            </div>
                                                        ) : (
                                                            <div className="bg-purple-300 p-[0.729vw] rounded-full shadow-lg hover:bg-purple-600">
                                                                <svg className="w-[1.782vw] h-[1.782vw] text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path
                                                                        strokeLinecap="round"
                                                                        strokeLinejoin="round"
                                                                        strokeWidth="2"
                                                                        d="M13 10V3L4 14h7v7l9-11h-7z"
                                                                    />
                                                                </svg>
                                                            </div>
                                                        )}
                                                    </button>

                                                    <p className="text-center text-[0.729vw] text-gray-600 max-w-[14.337vw]">
                                                        {enhancementStatus.processing
                                                            ? "We're enhancing your image. Please wait a moment..."
                                                            : "Your image is low resolution. Enhance it !"}
                                                    </p>
                                                </div>
                                            </div>

                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Action Buttons */}
                            <div className="mt-[1.053vw] flex justify-end gap-[0.5265vw]">
                                <button
                                    type="button"
                                    onClick={handleModalCancel}
                                    className="px-[0.891vw] py-[0.3645vw] text-[0.648vw] font-medium text-gray-600 hover:text-gray-800 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={handleSaveEnhancedImage}
                                    style={{ color: '#fff' }}
                                    className="px-[0.891vw] py-[0.3645vw] text-[0.648vw] font-medium text-white bg-blue-500 rounded-[0.3645vw] hover:bg-purple-600 transition-colors shadow-sm hover:shadow-md"
                                >
                                    Save Image
                                </button>
                            </div>
                        </div>
                    </Modal>
                )}
            </>
        );
    };

export default FotorImageUpload;
